import { autoinject, computedFrom, customElement, observable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { watch } from 'aurelia-watch-decorator';
import { FilesUploadController } from 'framework/script_utilities/files-upload-controller';
import { FileParameter, UploadedFile } from 'model/essential-types';

@autoinject
@customElement('request-attachments-panel')
export class RequestAttachmentsPanelCustomElement {
  @bindable headingIntro: string;
  @bindable controller: RequestAttachmentsController;
  @bindable disabled: boolean;
  fileInput: HTMLInputElement;
  @observable selectedFiles: FileList = null;
  attachments: UploadedFileWrapper[] = [];

  attached() {
    this.clearFileInput();
  }

  @watch((x: RequestAttachmentsPanelCustomElement) => x.controller.attachments)
  updateAttachments(attachments: UploadedFile[]) {
    this.attachments = attachments.map(x => ({ uploadingFileName: null, uploadedFile: x, isProcessing: false } as UploadedFileWrapper));
  }

  selectedFilesChanged(files: FileList) {
    if (files != null) {
      if (this.controller.maxAllowedAttachmensCount != null && this.attachments.length + files.length > this.controller.maxAllowedAttachmensCount) {
        this.controller.maximumAllowedAttachmentsExceeded();
      }
      else {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const uploadedFileWrapper = { uploadingFileName: file.name, uploadedFile: null, isProcessing: true } as UploadedFileWrapper;
          this.attachments.push(uploadedFileWrapper);
          this.controller.filesUploadController.uploadFile(() => {
            return this.controller.upload({ fileName: file.name, data: file } as FileParameter)
              .then(x => {
                uploadedFileWrapper.uploadedFile = x;
                uploadedFileWrapper.isProcessing = false;
              })
              .catch(() => {
                this.attachments.splice(this.attachments.indexOf(uploadedFileWrapper), 1);
              });
          });
        }
      }
      this.clearFileInput();
    }
  }

  downloadAttachment(attachment: UploadedFileWrapper) {
    attachment.isProcessing = true;
    this.controller.download(attachment.uploadedFile)
      .finally(() => attachment.isProcessing = false);
  }

  removeAttachment(attachment: UploadedFileWrapper) {
    this.attachments.splice(this.attachments.indexOf(attachment), 1);
    this.controller.remove(attachment.uploadedFile);
  }

  @computedFrom('controller.allowedFileExtensions')
  get allowedFileExtensions(): string {
    return this.controller.allowedFileExtensions.join(',');
  }

  @computedFrom('disabled', 'controller.maxAllowedAttachmensCount', 'attachments.length')
  get showSelectFileInput(): boolean {
    return !this.disabled && (this.controller.maxAllowedAttachmensCount == null || this.attachments.length < this.controller.maxAllowedAttachmensCount);
  }

  private clearFileInput() {
    this.selectedFiles = null;
    this.fileInput.value = null;
  }
}

export interface UploadedFileWrapper {
  uploadingFileName: string | null;
  uploadedFile: UploadedFile | null;
  isProcessing: boolean;
}

export interface RequestAttachmentsController {
  attachments: UploadedFile[];
  maxAllowedAttachmensCount: number | null;
  allowedFileExtensions: string[];
  filesUploadController: FilesUploadController;
  maximumAllowedAttachmentsExceeded(): void;
  download(attachment: UploadedFile): Promise<void>;
  upload(attachment: FileParameter): Promise<UploadedFile>;
  remove(attachment: UploadedFile): void;
}
